import { useEffect, useState } from 'react';
import { CSVLink } from 'react-csv/lib';
import { DownloadIcon } from '@entur/icons';
import { useIntl } from 'react-intl';
import { IconButton } from '@entur/button';
import { Tooltip } from '@entur/tooltip';
import { sortByIndex } from 'components/Table/cell-renderers/parseCellValue';
import { LegacyColumns } from 'components/Table/types';

interface DownloadCsvProps {
  columns: LegacyColumns;
  data: Record<string, unknown>[];
  tooltipLabel?: string;
}

const DownloadCsv = ({ columns, data, tooltipLabel }: DownloadCsvProps) => {
  const intl = useIntl();
  const [savedColumns, setSavedColumns] = useState<LegacyColumns | undefined>(undefined);
  const [handledData, setHandledData] = useState<Record<string, string>[]>([]);

  useEffect(() => {
    const handleLocalStorage = (event: CustomEvent<{ tableKey: string }>) => {
      const savedItem = window.localStorage.getItem(`saved${event.detail.tableKey}Columns`);

      if (savedItem) {
        setSavedColumns(JSON.parse(savedItem));
      }
    };

    window.addEventListener('local_storage', handleLocalStorage);

    return () => {
      window.removeEventListener('local_storage', handleLocalStorage);
    };
  }, []);

  const convertColumnsToHeaders = (cols: LegacyColumns) => {
    const headers: { label: string; key: string }[] = [];

    if (cols) {
      Object.entries(savedColumns || cols)
        .sort(sortByIndex)
        .forEach((obj) => {
          const colKey = obj[0];
          const options = obj[1];
          const key = colKey.split('.').join('');

          const label = options.headerKey ? intl.formatMessage({ id: options.headerKey }) : (options.text ?? '');
          headers.push({ label, key });
        });
    }

    return headers;
  };

  const handleData = () => {
    const getData = () => {
      if (!columns || Object.keys(columns).length === 0) return data;
      return data.map((item) => {
        return Object.keys(columns).reduce(
          (prev, curr) => ({
            ...prev,
            [curr.split('.').join('')]: item[curr] || null,
          }),
          {}
        );
      });
    };
    setHandledData(getData());
  };

  return (
    <CSVLink
      filename="cleos.csv"
      data={handledData}
      headers={convertColumnsToHeaders(columns)}
      separator=";"
      tabIndex={-1}
      onClick={handleData}
    >
      <Tooltip placement="bottom" content={tooltipLabel || 'Last ned CSV'}>
        <IconButton>
          <DownloadIcon />
        </IconButton>
      </Tooltip>
    </CSVLink>
  );
};

export default DownloadCsv;
